
/*
 * Configuration values needed in js codes
 *
 * NOTE:
 * Please use the following command to avoid accidentally committing personal changes
 * git update-index --assume-unchanged packages/shared/src/utils/config.js
 *
 */

export const livechat_license_id = 12049137;
export const livechat_client_id = '66aa088aad5a414484c1fd1fa8a5ace7';

export const domain_app_ids = {
    // these domains as supported "production domains"
    'deriv.app': 16929, // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
    'app.deriv.com': 16929,
    'staging-app.deriv.com': 16303,
    'app.deriv.me': 1411,
    'staging-app.deriv.me': 1411, // TODO: setup staging for deriv.me
    'app.deriv.be': 30767,
    'staging-app.deriv.be': 31186,
    'binary.com': 1,
    'test-app.deriv.com': 51072,
};

export const platform_app_ids = {
    derivgo: 23789,
};

export const getCurrentProductionDomain = () =>
    !/^staging\./.test(window.location.hostname) &&
    Object.keys(domain_app_ids).find(domain => window.location.hostname === domain);

export const isProduction = () => {
    const all_domains = Object.keys(domain_app_ids).map(domain => `(www\\.)?${domain.replace('.', '\\.')}`);
    return new RegExp(`^(${all_domains.join('|')})$`, 'i').test(window.location.hostname);
};

export const isTestLink = () => {
    return /^((.*)\.binary\.sx)$/i.test(window.location.hostname);
};

export const isLocal = () => /localhost(:\d+)?$/i.test(window.location.hostname);

const STORAGE_KEY = 'config.app_id';

/** Numeric APP_ID from parent build (written to `config.app_id`). */
const EVERYMAN_SYNC_APP_ID_KEY = 'everyman_sync_app_id';
/** OAUTH_CLIENT_ID from parent build (PKCE client id string). */
const EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY = 'everyman_sync_oauth_client_id';
/**
 * Numeric **Deriv-App-ID** for V2 Options REST + OTP WS (matches PU `getOAuthDerivAppIdForEverymanSync`).
 * Separate from legacy WS `app_id` when `OAUTH_DERIV_APP_ID` differs from `APP_ID`.
 */
export const EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY = 'everyman_sync_oauth_deriv_app_id';

/**
 * True when dtrader-c runs **inside a parent frame** (any embedding site — query-auth only, no cookies).
 * No hostname allowlist — every site may embed this app and must pass the session via URL query.
 */
export const isEmbeddedPuIframe = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.self !== 'undefined' &&
    typeof window.top !== 'undefined' &&
    window.self !== window.top;

/** @deprecated Use `isEmbeddedPuIframe`; name kept for older imports. */
export const isEverymanDeploymentHost = isEmbeddedPuIframe;

const DEV_APP_ID = 113222;

/** Local / IP dev shell default; embedded apps get `app_id` from sync cookie, `?app_id=`, or bridge. */
const getAppIdForDomain = (): string => {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname.toLowerCase();
    if (isLocal() || /^[\d.]+$/.test(host) || host.endsWith('.localhost')) {
        return String(DEV_APP_ID);
    }
    return '';
};

/**
 * Resolve numeric WS `app_id`. In a PU iframe prefer LS from parent sync cookie / bridge, then `?app_id=`.
 */
const getAppIdFromUrlOrConstant = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();

    if (isEmbeddedPuIframe()) {
        try {
            const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
            if (stored && /^\d+$/.test(stored)) return stored;
        } catch {
            /* ignore */
        }
        try {
            const params = new URLSearchParams(window.location.search);
            const fromUrl = params.get('app_id');
            if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
        } catch {
            /* ignore */
        }
        /*
         * The legacy numeric app id is unused by V2 OAuth2 sessions (`Deriv-App-ID` is the OAuth
         * client id — see `getOAuthDerivAppIdFromEverymanSync`). Only warn for legacy deploys;
         * query-auth sessions (auth_flow=oauth2) never need `?app_id=` and must not log like they do.
         */
        let is_oauth2_session = false;
        try {
            is_oauth2_session = localStorage.getItem('auth_flow') === 'oauth2';
        } catch {
            /* ignore */
        }
        if (!is_oauth2_session) {
            // eslint-disable-next-line no-console
            console.error(
                '[embed-sync] Missing valid config.app_id. Pass ?app_id= on the iframe URL (legacy tooling only — V2 auth uses the OAuth client id).'
            );
        }
        return '';
    }

    try {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('app_id');
        if (fromUrl && /^\d+$/.test(fromUrl)) return fromUrl;
    } catch {
        // ignore
    }
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored && /^\d+$/.test(stored)) return stored;
    } catch {
        // ignore
    }
    return getAppIdForDomain();
};

/**
 * Resolve app_id (from URL if present, else APP_ID) and store in localStorage.
 * Only updates localStorage when the value is missing or different.
 * Call once at app bootstrap (lowest level); rest of the app reads from localStorage via getAppId().
 */
export const syncAppIdFromDomain = (): void => {
    if (typeof window === 'undefined') return;
    const resolved = getAppIdFromUrlOrConstant();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored !== resolved) {
            localStorage.setItem(STORAGE_KEY, resolved);
        }
    } catch {
        // ignore
    }
};

/**
 * Returns app_id from localStorage (set once on page load by syncAppIdFromDomain).
 * If storage is empty (e.g. before sync ran), runs sync once then returns.
 * @deprecated Please use 'WebSocketUtils.getAppId' from '@deriv-com/utils' instead of this.
 */
export const getAppId = (): string => {
    if (typeof window === 'undefined') return getAppIdForDomain();
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored) return stored;
    } catch {
        // ignore
    }
    syncAppIdFromDomain();
    const resolved = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
    if (resolved) return resolved;
    if (isEmbeddedPuIframe()) return '';
    return getAppIdForDomain();
};

/**
 * V2 REST + OTP `Deriv-App-ID` header value. On V2 this is the **OAuth2 client id** (alphanumeric,
 * e.g. `33aka1bh4kcJkLkZo6f99`), never the legacy numeric app id — see root config.ts parity note.
 * Resolution: parent sync LS (`everyman_sync_oauth_deriv_app_id`) → build/parent client id → env.
 */
export const getOAuthDerivAppIdFromEverymanSync = (): string => {
    if (typeof window === 'undefined') return getOAuth2ClientIdFromConfig();
    try {
        const from_sync = localStorage.getItem(EVERYMAN_SYNC_OAUTH_DERIV_APP_ID_KEY)?.trim();
        if (from_sync) return from_sync;
    } catch {
        /* ignore */
    }
    return getOAuth2ClientIdFromConfig();
};

/**
 * V2 OAuth2 app identity (`Deriv-App-ID`) for this session.
 * Resolution: parent query handoff (`?client_id=…` → `everyman_sync_oauth_client_id`) →
 * build/env `OAUTH_CLIENT_ID`. There is intentionally **no hard-coded fallback** — every embedding
 * site must pass `?client_id=<the app id that issued the access token>`; a mismatched app id fails
 * auth on Deriv's side.
 */
export const getOAuth2ClientIdFromConfig = (): string => {
    if (typeof window !== 'undefined') {
        try {
            const from_ls = localStorage.getItem(EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY)?.trim();
            if (from_ls) return from_ls;
        } catch {
            /* ignore */
        }
    }
    const from_env =
        typeof process !== 'undefined' && process.env && typeof process.env.OAUTH_CLIENT_ID === 'string'
            ? process.env.OAUTH_CLIENT_ID.trim()
            : '';
    return from_env;
};

/** True when parent sync cookie applied V2 identifiers (PKCE client + Deriv-App-ID), not V1 `APP_ID` alone. */
export const isEverymanSyncV2OAuthClientMode = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        const id = localStorage.getItem(EVERYMAN_SYNC_OAUTH_CLIENT_ID_KEY)?.trim();
        return !!id;
    } catch {
        return false;
    }
};

/**
 * Returns the Deriv Bot base URL for the current app.
 */
export const getBotBaseUrl = (): string => {
    return 'https://bbot.botblitz.site';
};

// Same as root: single production WebSocket URL (ws.derivws.com), optional localStorage override
export const getSocketURL = (_is_wallets = false) => {
    const local_storage_server_url = window.localStorage.getItem('config.server_url');
    if (local_storage_server_url) return local_storage_server_url;
    return 'ws.derivws.com';
};

export const checkAndSetEndpointFromUrl = () => {
    if (isTestLink()) {
        const url_params = new URLSearchParams(location.search.slice(1));

        if (url_params.has('qa_server')) {
            const qa_server = url_params.get('qa_server') || '';

            url_params.delete('qa_server');

            if (/^(^(www\.)?qa[0-9]{1,4}\.deriv.dev|(.*)\.derivws\.com)$/.test(qa_server)) {
                localStorage.setItem('config.server_url', qa_server);
            }

            const params = url_params.toString();
            const hash = location.hash;

            location.href = `${location.protocol}//${location.hostname}${location.pathname}${
                params ? `?${params}` : ''
            }${hash || ''}`;

            return true;
        }
    }

    return false;
};

export const getDebugServiceWorker = () => {
    const debug_service_worker_flag = window.localStorage.getItem('debug_service_worker');
    if (debug_service_worker_flag) return !!parseInt(debug_service_worker_flag);

    return false;
};
